import React, { useState } from 'react';
import { Play, Shield } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';

const leadSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  phone: z.string().trim().min(10, "Phone number must be at least 10 digits").max(15, "Phone number is too long")
});

interface VSLOptInFormProps {
  onSuccess: (leadData: { id: string; name: string; email: string; phone: string }) => void;
}

const VSLOptInForm: React.FC<VSLOptInFormProps> = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate with zod
    const result = leadSchema.safeParse(formData);
    if (!result.success) {
      const newErrors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          newErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const trimmedName = formData.name.trim();
      const trimmedEmail = formData.email.trim();
      const trimmedPhone = formData.phone.trim();
      
      // Insert-only operation - no SELECT permissions needed for security
      // Use insert with onConflictDoNothing to handle duplicates silently
      const { error } = await supabase
        .from('vsl_leads')
        .insert({
          name: trimmedName,
          email: trimmedEmail,
          phone: trimmedPhone
        });

      // Ignore duplicate key errors (email already exists) - this is expected
      if (error && !error.message.includes('duplicate key')) {
        throw error;
      }

      toast({
        title: "Access Granted!",
        description: "Enjoy the video presentation.",
      });

      // Generate a client-side ID for UI purposes only
      // The actual ID is stored in the database but we don't need to read it back
      onSuccess({
        id: crypto.randomUUID(),
        name: trimmedName,
        email: trimmedEmail,
        phone: trimmedPhone
      });

    } catch (error) {
      console.error('Opt-in error:', error);
      toast({
        title: "Something went wrong",
        description: "Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto bg-white p-8 rounded-2xl shadow-2xl">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-4">
          <Play className="w-8 h-8 text-white ml-1" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Discover the AI SuperCreators Program™</h3>
        <p className="text-gray-600">See how your child can become a future-ready AI creator</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 rounded-lg border ${errors.name ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-400 text-lg`}
            placeholder="Your Name"
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
        </div>

        <div>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 rounded-lg border ${errors.email ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-400 text-lg`}
            placeholder="Your Email"
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>

        <div>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 rounded-lg border ${errors.phone ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-400 text-lg`}
            placeholder="Your Phone Number"
          />
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 py-4 rounded-lg text-white text-lg font-bold transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:transform-none"
        >
          {isSubmitting ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              <span>Loading...</span>
            </div>
          ) : (
            <>
              <Play className="w-5 h-5" />
              <span>Watch Now - It's Free</span>
            </>
          )}
        </button>

        <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mt-4">
          <Shield className="w-4 h-4" />
          <span>Your information is 100% secure</span>
        </div>
      </form>
    </div>
  );
};

export default VSLOptInForm;
